import {
  IChartApi,
  ISeriesApi,
  MouseEventParams,
  Time,
  CandlestickData
} from 'lightweight-charts';
import { MutableRefObject } from 'react';
import dayjs from 'dayjs';
import { Trade } from '@server/data-feed/schemas/output.schema';
import { toNumericTimeOrNull } from './time-normalization.utils';

type ExecutionTrade = Trade & { time: Time };
type ExecutionMarkers = {
  entries?: ExecutionTrade[];
  adds?: ExecutionTrade[];
  exits?: ExecutionTrade[];
  stopLosses?: ExecutionTrade[];
  takeProfits?: ExecutionTrade[];
};

const TRADE_MARKER_HOVER_RADIUS_PX = 14;
const POSITIVE_PNL_COLOR = '#43d787';
const NEGATIVE_PNL_COLOR = '#ff6b6b';
const NEUTRAL_PNL_COLOR = '#e8e8e8';

const hideTooltip = (
  tooltipRef: MutableRefObject<HTMLDivElement | null>
): void => {
  if (tooltipRef.current) {
    tooltipRef.current.style.display = 'none';
  }
};

const formatNumber = (
  value: number | undefined,
  maxFractionDigits = 2
): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  return value.toLocaleString(undefined, { maximumFractionDigits: maxFractionDigits });
};

const formatPrice = (value: number | undefined): string =>
  formatNumber(value, 8);

const formatSignedNumber = (
  value: number | undefined,
  maxFractionDigits = 2
): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  const abs = formatNumber(Math.abs(value), maxFractionDigits);
  if (value > 0) return `+${abs}`;
  if (value < 0) return `-${abs}`;
  return `0`;
};

const getPnlColor = (value: number | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return NEUTRAL_PNL_COLOR;
  }
  if (value > 0) return POSITIVE_PNL_COLOR;
  if (value < 0) return NEGATIVE_PNL_COLOR;
  return NEUTRAL_PNL_COLOR;
};

const formatTime = (time: Time): string => {
  const numericTime = toNumericTimeOrNull(time);
  if (numericTime == null) return String(time);
  return dayjs.unix(numericTime).format('YYYY-MM-DD HH:mm:ss');
};

const flattenExecutionTrades = (
  executionMarkers: ExecutionMarkers
): ExecutionTrade[] => [
  ...(executionMarkers.entries ?? []),
  ...(executionMarkers.adds ?? []),
  ...(executionMarkers.exits ?? []),
  ...(executionMarkers.stopLosses ?? []),
  ...(executionMarkers.takeProfits ?? [])
];

const getTradeDisplayPrice = (
  trade: ExecutionTrade,
  candle?: CandlestickData
): number | null => {
  if (
    (trade.type === 'EXIT' ||
      trade.type === 'STOP_LOSS' ||
      trade.type === 'TAKE_PROFIT') &&
    typeof trade.exitPrice === 'number'
  ) {
    return trade.exitPrice;
  }

  if ((trade.type === 'ENTRY' || trade.type === 'ADD') && candle) {
    return trade.side === 'LONG' ? candle.low : candle.high;
  }

  if (typeof trade.entryPrice === 'number') {
    return trade.entryPrice;
  }

  if (typeof trade.exitPrice === 'number') {
    return trade.exitPrice;
  }

  return null;
};

const getCandleByTime = (
  candlestickData: CandlestickData[],
  time: Time
): CandlestickData | undefined => {
  const hoveredTime = toNumericTimeOrNull(time);
  if (hoveredTime == null) return undefined;

  return candlestickData.find(
    (candle) => toNumericTimeOrNull(candle.time) === hoveredTime
  );
};

const setTradeTooltip = (
  tooltipRef: MutableRefObject<HTMLDivElement | null>,
  trade: ExecutionTrade,
  x: number,
  y: number
): void => {
  if (!tooltipRef.current) return;
  const pnlPercent = trade.pnlPercent * 100;
  const pnlColor = getPnlColor(trade.pnl);
  const pnlPercentColor = getPnlColor(pnlPercent);

  tooltipRef.current.innerHTML = `
    <div><strong>${trade.type}</strong> · ${trade.side}</div>
    <div>${formatTime(trade.time)}</div>
    <div><strong>Entry:</strong> ${formatPrice(trade.entryPrice)}</div>
    ${
      typeof trade.exitPrice === 'number'
        ? `<div><strong>Exit:</strong> ${formatPrice(trade.exitPrice)}</div>`
        : ''
    }
    <div><strong>Size:</strong> ${formatNumber(trade.size, 8)}</div>
    <div><strong>Remaining:</strong> ${formatNumber(
      trade.remainingPositionSize,
      8
    )}</div>
    <div><strong>PnL:</strong> <span style="color:${pnlColor}">${formatSignedNumber(trade.pnl, 8)}</span></div>
    <div><strong>PnL %:</strong> <span style="color:${pnlPercentColor}">${formatSignedNumber(pnlPercent)}%</span></div>
    <div><strong>Commission:</strong> ${formatNumber(trade.commission, 8)}</div>
  `;

  tooltipRef.current.style.left = `${x}px`;
  tooltipRef.current.style.top = `${y - 10}px`;
  tooltipRef.current.style.transform = 'translate(-50%, -100%)';
  tooltipRef.current.style.display = 'block';
};

export const handleTooltip = (
  param: MouseEventParams<Time>,
  tooltipRef: MutableRefObject<HTMLDivElement | null>,
  chartRef: MutableRefObject<IChartApi | null>,
  candlestickSeriesRef: MutableRefObject<ISeriesApi<'Candlestick'> | null>,
  candlestickData: CandlestickData[],
  executionMarkers: ExecutionMarkers
) => {
  if (!tooltipRef.current || !param.time || !param.point) {
    hideTooltip(tooltipRef);
    return;
  }

  const timeCoordinate = chartRef.current
    ?.timeScale()
    .timeToCoordinate(param.time);
  const hoveredCandle = getCandleByTime(candlestickData, param.time);

  if (timeCoordinate != null) {
    const hoveredTime = toNumericTimeOrNull(param.time);
    const tradesAtHoveredTime = flattenExecutionTrades(executionMarkers).filter(
      (trade) => {
        const tradeTime = toNumericTimeOrNull(trade.time);
        return tradeTime != null && tradeTime === hoveredTime;
      }
    );

    let bestHoveredTrade:
      | { trade: ExecutionTrade; x: number; y: number; distance: number }
      | undefined;

    for (const trade of tradesAtHoveredTime) {
      const displayPrice = getTradeDisplayPrice(trade, hoveredCandle);
      if (displayPrice == null) continue;

      const yCoordinate =
        candlestickSeriesRef.current?.priceToCoordinate(displayPrice);
      if (yCoordinate == null) continue;

      const dx = Math.abs(param.point.x - timeCoordinate);
      const dy = Math.abs(param.point.y - yCoordinate);
      const distance = Math.hypot(dx, dy);

      if (distance > TRADE_MARKER_HOVER_RADIUS_PX) continue;
      if (!bestHoveredTrade || distance < bestHoveredTrade.distance) {
        bestHoveredTrade = {
          trade,
          x: timeCoordinate,
          y: yCoordinate,
          distance
        };
      }
    }

    if (bestHoveredTrade) {
      setTradeTooltip(
        tooltipRef,
        bestHoveredTrade.trade,
        bestHoveredTrade.x,
        bestHoveredTrade.y
      );
      return;
    }
  }

  const candle = hoveredCandle;
  if (!candle) {
    hideTooltip(tooltipRef);
    return;
  }

  // Calculate the candlestick's visual bounds
  const highCoordinate = candlestickSeriesRef.current?.priceToCoordinate(
    candle.high
  );
  const lowCoordinate = candlestickSeriesRef.current?.priceToCoordinate(
    candle.low
  );

  if (
    timeCoordinate != null &&
    highCoordinate != null &&
    lowCoordinate != null
  ) {
    const mouseX = param.point.x;
    const mouseY = param.point.y;

    // Define hover bounds around the candlestick
    const hoverPadding = 5;
    const candlestickWidth = 10; // Approximate width of the candlestick
    const leftBound = timeCoordinate - candlestickWidth / 2 - hoverPadding;
    const rightBound = timeCoordinate + candlestickWidth / 2 + hoverPadding;
    const topBound = highCoordinate - hoverPadding;
    const bottomBound = lowCoordinate + hoverPadding;

    if (
      mouseX >= leftBound &&
      mouseX <= rightBound &&
      mouseY >= topBound &&
      mouseY <= bottomBound
    ) {
      // Update tooltip content
      tooltipRef.current.innerHTML = `
          <div>${formatTime(candle.time)}</div>
          <div><strong>Open:</strong> ${candle.open}</div>
          <div><strong>High:</strong> ${candle.high}</div>
          <div><strong>Low:</strong> ${candle.low}</div>
          <div><strong>Close:</strong> ${candle.close}</div>
        `;

      tooltipRef.current.style.left = `${timeCoordinate}px`;
      tooltipRef.current.style.top = `${highCoordinate - 30}px`; // Position above the high price
      tooltipRef.current.style.transform = 'translate(-50%, -100%)';
      tooltipRef.current.style.display = 'block';
    } else {
      hideTooltip(tooltipRef);
    }
  } else {
    hideTooltip(tooltipRef);
  }
};
